import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  auftragsSpeicherFuerGeraet,
  exklusiv,
  fehlerzeilen,
  gibFrei,
  raeumeAuf,
  verarbeiteWarteschlange,
  verwirfKonflikt,
  type AuftragsInhalt,
  type Buchungsauftrag,
  type LaufErgebnis,
  type Sender,
  type Wartegrund,
} from '../lager-warteschlange';

const NACHSENDEN_MS = 30_000;

/**
 * Warteschlange der Lagerbuchungen eines Kontexts (Kasse oder Handterminal): lädt die
 * Aufträge aus IndexedDB, sendet beim Start, bei Wiederverbindung, nach jedem neuen
 * Auftrag und alle 30 Sekunden. `sende` muss stabil sein (useCallback/useMemo).
 */
export function useLagerWarteschlange(
  kontext: string | null,
  sende: Sender | null,
  nachLauf?: (r: LaufErgebnis) => void,
) {
  const { speicher, dauerhaft } = useMemo(() => auftragsSpeicherFuerGeraet(), []);
  const [auftraege, setAuftraege] = useState<Buchungsauftrag[]>([]);
  const [sendet, setSendet] = useState(false);
  const nachLaufRef = useRef(nachLauf);
  nachLaufRef.current = nachLauf;
  // Nach Unmount (Abmeldung, Gerätewechsel) sendet ein noch laufender Lauf nichts mehr.
  const gemountet = useRef(true);
  useEffect(() => {
    gemountet.current = true;
    return () => {
      gemountet.current = false;
    };
  }, []);

  const neuLaden = useCallback(async () => {
    if (kontext === null) {
      setAuftraege([]);
      return;
    }
    setAuftraege(await speicher.alle(kontext));
  }, [kontext, speicher]);

  const senden = useCallback(
    async (manuell: string | null = null): Promise<LaufErgebnis | null> => {
      if (kontext === null || sende === null || !gemountet.current) {
        return null;
      }
      setSendet(true);
      try {
        const r = await exklusiv(kontext, () =>
          verarbeiteWarteschlange(
            speicher,
            kontext,
            sende,
            () => new Date(),
            manuell,
            () => !gemountet.current,
          ),
        );
        await raeumeAuf(speicher, kontext, new Date());
        nachLaufRef.current?.(r);
        return r;
      } catch {
        return null; // Ablage nicht lesbar – nächster Versuch beim nächsten Anlass
      } finally {
        setSendet(false);
        await neuLaden();
      }
    },
    [kontext, sende, speicher, neuLaden],
  );

  useEffect(() => {
    void neuLaden().then(() => senden());
    const online = (): void => {
      void senden();
    };
    window.addEventListener('online', online);
    const timer = setInterval(() => {
      if (navigator.onLine) {
        void senden();
      }
    }, NACHSENDEN_MS);
    return () => {
      window.removeEventListener('online', online);
      clearInterval(timer);
    };
  }, [neuLaden, senden]);

  /**
   * Auftrag zuerst dauerhaft ablegen, dann senden. Liefert den abgelegten Inhalt – bei
   * schon vorhandener Kennung (Absturz, zweiter Tab) den früher abgelegten, damit die Liste
   * genau dessen Mengen abzieht. Wirft, wenn das Ablegen scheitert oder der Browser nicht
   * dauerhaft speichern kann (dann bleibt die Liste stehen).
   */
  const einreihen = useCallback(
    async (a: Buchungsauftrag): Promise<AuftragsInhalt> => {
      if (!dauerhaft) {
        throw new Error('Dieser Browser kann Buchungen nicht dauerhaft speichern');
      }
      let gespeichert = a.inhalt;
      try {
        await speicher.lege(a);
      } catch (e: unknown) {
        const frueher = (await speicher.alle(a.inhalt.kontext)).find(
          (x) => x.inhalt.vorgangId === a.inhalt.vorgangId,
        );
        if (frueher === undefined) {
          throw e;
        }
        gespeichert = frueher.inhalt;
      }
      await neuLaden();
      void senden();
      return gespeichert;
    },
    [dauerhaft, speicher, neuLaden, senden],
  );

  /** Schon abgelegter Auftrag mit dieser Kennung (Abgleich der Liste beim Laden). */
  const finde = useCallback(
    async (vorgangId: string): Promise<AuftragsInhalt | null> => {
      if (kontext === null) {
        return null;
      }
      const a = (await speicher.alle(kontext)).find((x) => x.inhalt.vorgangId === vorgangId);
      return a?.inhalt ?? null;
    },
    [kontext, speicher],
  );

  const freigeben = useCallback(
    async (gruende: readonly Wartegrund[]): Promise<void> => {
      if (kontext === null) {
        return;
      }
      // Unter derselben Sperre wie die Sendeläufe: kein Überschreiben von „Versuch läuft“
      await exklusiv(kontext, () => gibFrei(speicher, kontext, gruende));
      await senden();
    },
    [kontext, speicher, senden],
  );

  const verwerfen = useCallback(
    async (a: Buchungsauftrag): Promise<void> => {
      await exklusiv(a.inhalt.kontext, async () => {
        // frischen Stand prüfen – nur ein echter Konflikt wird verworfen
        const frisch = (await speicher.alle(a.inhalt.kontext)).find(
          (x) => x.inhalt.vorgangId === a.inhalt.vorgangId,
        );
        if (frisch !== undefined) {
          await verwirfKonflikt(speicher, frisch);
        }
      });
      await neuLaden();
    },
    [speicher, neuLaden],
  );

  /** Fehlerzeilen wurden in die Liste übernommen – nur einmal anbieten. */
  const fehlerUebernommen = useCallback(
    async (a: Buchungsauftrag): Promise<void> => {
      await exklusiv(a.inhalt.kontext, async () => {
        const frisch = (await speicher.alle(a.inhalt.kontext)).find(
          (x) => x.inhalt.vorgangId === a.inhalt.vorgangId,
        );
        if (frisch !== undefined) {
          await speicher.setzeZustand(a.inhalt.vorgangId, {
            ...frisch.zustand,
            fehlerUebernommen: true,
          });
        }
      });
      await neuLaden();
    },
    [speicher, neuLaden],
  );

  return {
    auftraege,
    sendet,
    dauerhaft,
    einreihen,
    senden,
    freigeben,
    verwerfen,
    fehlerUebernommen,
    finde,
    neuLaden,
  };
}

export type ListenSperre = 'warten' | 'eigen' | 'fremd' | 'unbekannt';

/**
 * Eine Erfassungsliste darf nur ein Fenster bzw. Tab bearbeiten (Web-Locks-API): sonst
 * überschreiben sich die Tabs gegenseitig den gemeinsamen localStorage-Eintrag. Bis die
 * Sperre gehalten wird („warten“), wird nichts geschrieben; mit Erhalt lädt `beiErhalt` die
 * Liste frisch. Ein zweiter Tab wartet („fremd“) und übernimmt, sobald der erste die Liste
 * freigibt. Ohne Web Locks (alte Browser): „unbekannt“ – dann gleicht der Aufrufer über das
 * storage-Ereignis ab.
 */
export function useListenSperre(name: string, beiErhalt: () => void): ListenSperre {
  const [zustand, setZustand] = useState<ListenSperre>(() =>
    (globalThis.navigator as { locks?: LockManager } | undefined)?.locks === undefined
      ? 'unbekannt'
      : 'warten',
  );
  const beiErhaltRef = useRef(beiErhalt);
  beiErhaltRef.current = beiErhalt;
  useEffect(() => {
    const locks = (globalThis.navigator as { locks?: LockManager } | undefined)?.locks;
    if (locks === undefined) {
      setZustand('unbekannt');
      return undefined;
    }
    setZustand('warten');
    let aktiv = true;
    let freigeben: () => void = () => undefined;
    const halten = new Promise<void>((r) => {
      freigeben = r;
    });
    const halte = async (): Promise<void> => {
      if (!aktiv) {
        return;
      }
      beiErhaltRef.current(); // Stand anderer Tabs übernehmen, bevor geschrieben wird
      setZustand('eigen');
      await halten;
    };
    void locks
      .request(`bilanx-liste-${name}`, { ifAvailable: true }, async (lock) => {
        if (lock !== null) {
          await halte();
          return;
        }
        if (aktiv) {
          setZustand('fremd');
        }
        void locks.request(`bilanx-liste-${name}`, halte);
      })
      .catch(() => {
        if (aktiv) {
          setZustand('unbekannt');
        }
      });
    return () => {
      aktiv = false;
      freigeben();
    };
  }, [name]);
  return zustand;
}

/** Darf dieser Tab die Liste ändern und speichern? */
export function listeBearbeitbar(s: ListenSperre): boolean {
  return s === 'eigen' || s === 'unbekannt';
}

const ART_TEXT = { wareneingang: 'Wareneingang', inventur: 'Inventur' } as const;

const WARTE_TEXT: Record<Wartegrund, string> = {
  verbindung: 'wartet auf Verbindung – wird automatisch gesendet',
  zugang: 'Zugang ungültig oder widerrufen – Gerät neu einrichten',
  recht: 'Anmeldung mit passendem Recht nötig – danach wird gesendet',
  pruefen: 'Inventur älter als 30 Minuten – bitte prüfen und „Jetzt senden“',
};

function zeit(iso: string): string {
  return new Date(iso).toLocaleString('de-AT', {
    timeZone: 'Europe/Vienna',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function zaehleAuftraege(auftraege: readonly Buchungsauftrag[]): {
  ausstehend: number;
  konflikt: number;
} {
  return {
    ausstehend: auftraege.filter((a) => a.zustand.status === 'ausstehend').length,
    konflikt: auftraege.filter((a) => a.zustand.status === 'konflikt').length,
  };
}

/**
 * Buchungen mit eindeutigem Zustand: gebucht / ausstehend / Konflikt. Ausstehende sendet
 * die Warteschlange selbst; „Jetzt senden“ schickt denselben Auftrag mit derselben
 * Kennung (nie doppelt). Ein Konflikt wird nur bewusst verworfen – vorher die
 * Bewegungshistorie des Artikels prüfen.
 */
export function LagerBuchungen(props: {
  auftraege: readonly Buchungsauftrag[];
  sendet: boolean;
  dauerhaft: boolean;
  onSenden: (vorgangId: string) => void;
  onVerwerfen: (a: Buchungsauftrag) => void;
  onFehlerZurueck: (a: Buchungsauftrag) => void;
}) {
  const liste = [...props.auftraege].reverse();
  const z = zaehleAuftraege(props.auftraege);
  return (
    <div className="karte lager-buchungen">
      <h2>
        Buchungen · {z.ausstehend} ausstehend
        {z.konflikt > 0 && <span className="fehler"> · {z.konflikt} Konflikt</span>}
        {props.sendet && <span className="hinweis"> · sendet …</span>}
      </h2>
      {!props.dauerhaft && (
        <p className="warnung">
          Dieser Browser kann Buchungen nicht dauerhaft speichern (IndexedDB fehlt) – Buchen ist
          gesperrt, die Liste bleibt erhalten. Bitte ein aktuelles Chrome verwenden.
        </p>
      )}
      {liste.length === 0 ? (
        <p className="hinweis">Noch keine Buchungen auf diesem Gerät.</p>
      ) : (
        <ul className="buchungsliste">
          {liste.map((a) => {
            const { inhalt, zustand } = a;
            const fehler = zustand.status === 'gebucht' ? fehlerzeilen(a) : [];
            const zurueckMoeglich = fehler.length > 0 && zustand.fehlerUebernommen !== true;
            const gebucht = (zustand.ergebnisse ?? []).filter((e) => e.ergebnis === 'gebucht');
            const gezaehlt = (zustand.ergebnisse ?? []).filter((e) => e.ergebnis === 'gezaehlt');
            const neu = (zustand.ergebnisse ?? []).filter((e) => e.ergebnis === 'vorschlag');
            return (
              <li key={inhalt.vorgangId} className={`buchung ${zustand.status}`}>
                <div className="buchung-kopf">
                  <span className={`buchung-status ${zustand.status}`}>
                    {zustand.status === 'gebucht'
                      ? 'gebucht'
                      : zustand.status === 'konflikt'
                        ? 'Konflikt'
                        : 'ausstehend'}
                  </span>
                  <strong>
                    {inhalt.laufId === undefined
                      ? ART_TEXT[inhalt.art]
                      : `Inventurlauf ${String(inhalt.laufId)}`}
                  </strong>
                  {inhalt.bezug !== '' && <span>{inhalt.bezug}</span>}
                  <span className="hinweis">
                    {inhalt.positionen.length} Pos. · {zeit(inhalt.erstelltAm)}
                    {inhalt.bediener === null ? '' : ` · ${inhalt.bediener}`}
                  </span>
                </div>
                {zustand.status === 'ausstehend' && (
                  <p className="hinweis">
                    {zustand.wartegrund === null ? 'wird gesendet' : WARTE_TEXT[zustand.wartegrund]}
                    {zustand.meldung !== null && ` (${zustand.meldung})`}
                  </p>
                )}
                {zustand.status === 'konflikt' &&
                  (zustand.konfliktArt === 'abgewiesen' ? (
                    <p className="fehler">
                      {zustand.meldung ?? 'Abgewiesen'} – der Server hat diesen Auftrag nicht
                      gebucht. Zeilen korrigieren und neu erfassen.
                    </p>
                  ) : (
                    <p className="fehler">
                      {zustand.meldung ?? 'Konflikt'} –{' '}
                      {zustand.konfliktArt === 'unklar'
                        ? 'ein früherer Versuch blieb ohne Antwort, der Auftrag kann also schon gebucht sein.'
                        : 'unter dieser Kennung liegt am Server ein anderer Inhalt, der schon gebucht sein kann.'}{' '}
                      <strong>Nicht einfach neu erfassen:</strong> erst Bestand und Bewegungen der
                      Artikel prüfen, dann entscheiden.
                    </p>
                  ))}
                {zustand.status === 'gebucht' && (
                  <p className="hinweis">
                    {inhalt.laufId === undefined
                      ? `${String(gebucht.length)} gebucht`
                      : `${String(gezaehlt.length)} gezählt (Bestand setzt der Abschluss)`}
                    {neu.length > 0 && `, ${String(neu.length)} als neuer Artikel vorgeschlagen`}
                    {fehler.length > 0 && `, ${String(fehler.length)} nicht gebucht`}
                    {zustand.wiederholt && ' – war schon gebucht (Wiederholung erkannt)'}
                  </p>
                )}
                {fehler.length > 0 && (
                  <ul className="fehler">
                    {fehler.map((f, i) => (
                      <li key={`${f.code}-${String(i)}`}>
                        {f.code}: {f.fehler}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="zeile">
                  {zustand.status === 'ausstehend' && (
                    <button
                      type="button"
                      className="taste zweit"
                      disabled={props.sendet}
                      onClick={() => {
                        if (
                          inhalt.art !== 'inventur' ||
                          window.confirm(
                            inhalt.laufId === undefined
                              ? 'Inventur jetzt senden? Der Bestand wird auf die gezählten Mengen gesetzt.'
                              : `Zählung jetzt an Inventurlauf ${String(inhalt.laufId)} senden? Den Bestand setzt erst der Abschluss im Backoffice.`,
                          )
                        ) {
                          props.onSenden(inhalt.vorgangId);
                        }
                      }}
                    >
                      Jetzt senden
                    </button>
                  )}
                  {zustand.status === 'konflikt' && (
                    <button
                      type="button"
                      className="taste zweit gefahr"
                      onClick={() => {
                        if (
                          window.confirm(
                            'Konflikt verwerfen? Der Auftrag wird nicht gebucht und vom Gerät entfernt.',
                          )
                        ) {
                          props.onVerwerfen(a);
                        }
                      }}
                    >
                      Verwerfen
                    </button>
                  )}
                  {zurueckMoeglich && (
                    <button
                      type="button"
                      className="taste zweit"
                      onClick={() => {
                        props.onFehlerZurueck(a);
                      }}
                    >
                      Nicht gebuchte Zeilen in die Liste
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
